import { memo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { ExternalLinkIcon, LinkedInIcon } from '../../../../components/Icons'
import { InfoCard } from '../../../../components/InfoCard'
import { useFetchLinkedInProfile } from '../../../../hooks/apiHooks/twinHooks'
import {
  useTwinData,
  useTwinPreview,
} from '../../../../hooks/storeHooks/useTwin'
import { useVirtualKeyboard } from '../../../../hooks/useVirtualKeyboard'
import { Heading } from '../Heading'

interface LinkedInTabProps {
  openNextTab: () => void
}

const LinkedInTab: React.FC<LinkedInTabProps> = memo(({ openNextTab }) => {
  const [linkedInUrl, setLinkedInUrl] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const { setLinkedInProfile } = useTwinData()
  const { setPreviewTabName } = useTwinPreview()

  useVirtualKeyboard([inputRef])

  const { mutateAsync: fetchLinkedInProfile, isPending } =
    useFetchLinkedInProfile()

  const validateLinkedInUrl = (url: string): string => {
    if (!url.trim()) {
      return 'Please enter a LinkedIn URL'
    }

    try {
      const urlObj = new URL(url)
      const hostname = urlObj.hostname.toLowerCase()
      const isLinkedInDomain =
        hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com')

      if (!isLinkedInDomain) {
        return 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)'
      }

      const pathname = urlObj.pathname.toLowerCase()
      if (!pathname.includes('/in/') && !pathname.includes('/pub/')) {
        return 'Please enter a LinkedIn profile URL (should contain /in/ or /pub/)'
      }

      return ''
    } catch {
      return 'Please enter a valid URL format (e.g., https://linkedin.com/in/username)'
    }
  }

  const validateImageUrl = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: 'HEAD' })
      return response.ok && response.status >= 200 && response.status < 300
    } catch {
      return false
    }
  }

  const handleContinue = async () => {
    const validationError = validateLinkedInUrl(linkedInUrl)
    if (validationError) {
      toast.error(validationError)
      return
    }

    try {
      const data = await fetchLinkedInProfile({
        url: linkedInUrl,
      })
      if (!data.success) {
        throw new Error('Failed to fetch LinkedIn profile')
      }

      let isImageUrlValid = false
      if (data.profileImageUrl) {
        isImageUrlValid = await validateImageUrl(data.profileImageUrl)
      }

      const profileDataToSave = {
        ...data,
        profileImageUrl: isImageUrlValid ? data.profileImageUrl : '',
      }

      if (isImageUrlValid) {
        setPreviewTabName('preview')
      }
      setLinkedInProfile(profileDataToSave)
      openNextTab()
    } catch (error) {
      console.error('Error fetching LinkedIn profile:', error)
      toast.error('Failed to fetch LinkedIn profile. Please try again.')
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkedInUrl(e.target.value)
  }

  return (
    <div className="max-w-[600px] mb-auto sm:mb-0">
      <Heading
        title="Start Smarter with LinkedIn"
        subtitle="We pre‑fill your bio and tailor the interview so your twin sounds
            like you from day one."
      />
      <label className="text-white text-base font-normal leading-[150%] mb-2 block">
        Paste your LinkedIn profile URL:
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id="linkedInUrl"
          type="url"
          value={linkedInUrl}
          onChange={handleUrlChange}
          placeholder="https://linkedin.com/in/johndoe"
          className="px-4 pl-12 h-[44px] text-white w-full  border-dark-neutral-500 focus:border-coralborder focus:outline-none shadow-[0_-0.5px_1px_0_rgba(255,255,255,0.30)_inset,0_-0.5px_1px_0_rgba(255,255,255,0.25)_inset,1px_1.5px_4px_0_rgba(0,0,0,0.08)_inset,1px_1.5px_4px_0_rgba(0,0,0,0.10)_inset] bg-[rgb(128_128_128/30%)] border-t-[1px] border-t-[rgb(255_255_255/50%)] rounded-2xl transition-colors duration-200 bg-blend-luminosity backdrop-blur-[50px]"
          disabled={isPending}
        />
        <LinkedInIcon
          size={21}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
        />
      </div>

      <div className="text-white/50 text-xs font-normal leading-[150%] flex align-middle justify-start mt-2 gap-2">
        <a
          data-testid="linkedin-url-help-link"
          href="https://www.linkedin.com/in/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex align-middle shrink-0"
        >
          Get your LinkedIn URL&nbsp;
          <ExternalLinkIcon size={12} />
        </a>
        <span className="bg-dark-neutral-400 w-[3px] h-[3px] rounded-full inline-block align-middle shrink-0"></span>
        <span className="shrink-0">Read‑only - we don’t post anything.</span>
      </div>

      <InfoCard
        title="Setup in seconds"
        description="Use LinkedIn to auto-build your profile."
        className="mt-8"
      />
      <InfoCard
        title="Real-you responses"
        description="Answers match how you speak and what you’ve done."
        className="mb-6"
      />

      <div className="flex gap-4 justify-center mt-8">
        <button
          data-testid="linkedin-skip-btn"
          className="btn btn-secondary sm:!grow-0 !grow"
          onClick={openNextTab}
          disabled={isPending}
        >
          Skip For Now
        </button>
        <button
          data-testid="linkedin-continue-btn"
          onClick={handleContinue}
          disabled={
            isPending ||
            !linkedInUrl.trim() ||
            !!validateLinkedInUrl(linkedInUrl)
          }
          className="btn btn-primary sm:!grow-0 !grow"
        >
          Continue
          {isPending ? (
            <AnimatedSpinner className="ml-2" />
          ) : (
            <i className="fa-solid fa-arrow-right ml-2" />
          )}
        </button>
      </div>
    </div>
  )
})

export { LinkedInTab }
